import { useState, useEffect } from 'react'
import { auth, db } from '@/lib/firebase'
import { doc, getDoc } from 'firebase/firestore'

const ADMIN_MODE_STORAGE_KEY = 'adminModeEnabled'

// Cache for admin status check to avoid repeated Firestore reads
let adminStatusCache: {
  userId: string | null
  isAdmin: boolean | null
  promise: Promise<boolean> | null
} = {
  userId: null,
  isAdmin: null,
  promise: null
}

async function checkAdminStatus(userId: string): Promise<boolean> {
  try {
    const userDocRef = doc(db, 'users', userId)
    const userDoc = await getDoc(userDocRef)
    
    if (userDoc.exists()) {
      const userData = userDoc.data()
      return userData.isAdmin === true
    }
    return false
  } catch (error) {
    console.error('[useAdminMode] Error checking admin status:', error)
    return false
  }
}

export function isCurrentUserAdmin(): boolean {
  const user = auth.currentUser
  
  // If no user is logged in, clear cache and return false
  if (!user?.uid) {
    if (adminStatusCache.userId !== null) {
      adminStatusCache = { userId: null, isAdmin: null, promise: null }
    }
    return false
  }

  // If user changed, clear the cache
  if (adminStatusCache.userId !== null && adminStatusCache.userId !== user.uid) {
    adminStatusCache = { userId: null, isAdmin: null, promise: null }
  }

  // If we have a cached result for this user, check both admin status AND admin mode
  if (adminStatusCache.userId === user.uid && adminStatusCache.isAdmin !== null) {
    return checkIsAdminModeActive(adminStatusCache.isAdmin)
  }

  // If we're already fetching for this user, return false for now (will update on next call)
  if (adminStatusCache.userId === user.uid && adminStatusCache.promise) {
    return false
  }

  // Start fetching admin status and cache the promise
  adminStatusCache.userId = user.uid
  adminStatusCache.isAdmin = null
  adminStatusCache.promise = checkAdminStatus(user.uid).then((isAdmin) => {
    adminStatusCache.isAdmin = isAdmin
    adminStatusCache.promise = null
    return isAdmin
  })

  // Return false initially, will be updated on subsequent calls
  return false
}

/**
 * Hook to manage admin mode toggle state.
 * Admin mode is only available if the user has isAdmin: true in their userData.
 * When toggled off, admins can view the site as regular users.
 */
export function useAdminMode() {
  const [adminModeEnabled, setAdminModeEnabled] = useState<boolean>(() => {
    const stored = localStorage.getItem(ADMIN_MODE_STORAGE_KEY)
    // Default to true (enabled) if not set
    return stored === null ? true : stored === 'true'
  })

  useEffect(() => {
    localStorage.setItem(ADMIN_MODE_STORAGE_KEY, String(adminModeEnabled))
  }, [adminModeEnabled])

  const toggleAdminMode = () => {
    setAdminModeEnabled(prev => !prev)
  }

  return {
    adminModeEnabled,
    toggleAdminMode,
    setAdminModeEnabled
  }
}

/**
 * Helper function to check if admin features should be shown.
 * Returns true only if user is an admin AND has admin mode enabled.
 */
export function checkIsAdminModeActive(userIsAdmin: boolean | undefined): boolean {
  if (!userIsAdmin) return false
  
  const stored = localStorage.getItem(ADMIN_MODE_STORAGE_KEY)
  const adminModeEnabled = stored === null ? true : stored === 'true'
  
  return adminModeEnabled
}
